import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { animated, useSpring, useTransition } from '@react-spring/web';

import WaveSurfer from 'wavesurfer.js';
import { OverlayTrigger, Popover } from 'react-bootstrap';

import { RECORD_NEW_RANGE, SYNC_RANGES, UPDATE_RANGE, useAnalytics } from 'src/pages/coaching/components/podcast/components/player/analytics';

import { SoundVisualizer } from 'src/pages/coaching/components/simulation/view/simulator/visualiser';
import { useDesktop } from 'src/components/hooks/useResponsive';
import { LearningRecordSimulation } from 'src/types/coaching/learners';

import _ from 'lodash';
import cx from 'classnames';
import styles from './player.module.scss';


interface PlayerProps {
  simulation?: LearningRecordSimulation;
  onPlay?: () => void;
}

export const Player: React.FC<PlayerProps> = ({ simulation, onPlay }) => {  
  const containerRef = React.useRef(null);
  const playerRef = React.useRef<WaveSurfer | null>(null);
  const currentRangeRef = React.useRef(null);
  const timerRef = React.useRef<any>(null);

  const desktop = useDesktop();

  const { url, segments, duration } = useMemo(() => {
    const url = _.get(simulation, 'report.callRecordingUrl') || "";
    const segments = _.get(simulation, 'segments', 1);
    const duration = _.get(simulation, 'duration', 0);

    return {
      url: url,
      segments: segments,
      duration: duration
    }
  }, [simulation]);

  const [analytics, dispatch] = useAnalytics(duration, segments);

  const [playerState, setPlayerState] = useState({
    muted: false,
    playbackRate: 1,
    volume: 100,
    duration: 0,
    currentTime: 0,
    waveformLoaded: false,
    audioPlaying: false
  });

  const loadingTransition = useTransition(!playerState.waveformLoaded, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const playerTransition = useTransition(playerState.waveformLoaded, {
    from: { opacity: 0, transform: 'translateY(100%)' },
    enter: { opacity: 1, transform: 'translateY(0%)' },
    leave: { opacity: 0, transform: 'translateY(100%)' },
  });

  const [playerStyles, playerAnimationRef] = useSpring({
    transform: playerState.waveformLoaded ? "translateY(0%)" : "translateY(100%)",
    opacity: playerState.waveformLoaded ? 1 : 0,
  }, [playerState.waveformLoaded]);

  useEffect(() => {
    if (url && WaveSurfer) {
      loadPlayer()
    }

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.error("Error destroying WaveSurfer instance: ", e);
        }

        playerRef.current = null;
      }
    };
  }, [url]);

  useEffect(() => {
    if (playerState.audioPlaying && onPlay) {
      onPlay();
    }
  }, [playerState.audioPlaying]);

  const loadPlayer = () => {
    const ctx = document.createElement('canvas').getContext('2d')
    const gradient = ctx!.createLinearGradient(0, 0, 0, 150)
    gradient.addColorStop(0, '#FF284F')
    gradient.addColorStop(0.15, '#FF439B')
    gradient.addColorStop(0.44, '#FF45CF')
    gradient.addColorStop(0.83, '#00A5B9')
    gradient.addColorStop(1, '#FF45CF')

    playerRef.current = WaveSurfer.create({
      container: containerRef.current!,
      height: 40,
      waveColor: '#BDBDBD',
      progressColor: gradient,
      barWidth: 2,
      barGap: 4,
      barRadius: 2,
      cursorWidth: 0,
      normalize: true,
      hideScrollbar: true,
    });

    playerRef.current.load(url);

    //Event listeners for player
    playerRef.current.on('ready', () => {
      setPlayerState((state) => {
        return {
          ...state,
          duration: Math.round(playerRef.current?.getDuration() || 0),
          waveformLoaded: true
        }
      });

      playerAnimationRef.start();
      // playerRef.current?.setTime(initialTimeSpent || 0);
    });

    playerRef.current.on('play', () => {
      setPlayerState((state) => {
        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
        }
      });
    });

    playerRef.current.on('pause', () => {
      setPlayerState((state) => {
        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
        }
      });
    });

    playerRef.current.on('audioprocess', () => {
      const endRange = playerRef.current?.getCurrentTime() || 0;

      setPlayerState((state) => {
        if ((endRange - state.currentTime) < 0.5) {
          return state;
        }

        if (currentRangeRef.current) {
          currentRangeRef.current = updateRange(playerRef.current?.getCurrentTime()) as any;
        } else {
          currentRangeRef.current = createRange(playerRef.current?.getCurrentTime()) as any;
        }

        return {
          ...state,
          currentTime: Math.round(playerRef.current?.getCurrentTime() || 0)
        }
      });
    });

    playerRef.current.on('seeking', () => {
      setPlayerState((state) => {
        currentRangeRef.current = createRange(playerRef.current?.getCurrentTime()) as any;

        return {
          ...state,
          currentTime: playerRef.current?.getCurrentTime() || 0,
        }
      });
    });

    playerRef.current.on('finish', () => {
      setPlayerState((state) => {
        if (currentRangeRef) {
          const currentTime = playerRef.current?.getCurrentTime() || 0;
          currentRangeRef.current = updateRange(currentTime) as any;
          syncRanges(currentTime);
        }

        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
          currentTime: 0
        }
      });
    });

    playerRef.current.on('error', (e) => {
      setPlayerState((state) => {
        return {
          ...state,
          error: e
        }
      })
    });

    timerRef.current = setInterval(() => {
      const currentTime = playerRef.current?.getCurrentTime() || 0;
      syncRanges(currentTime);
    }, 30000);
  };

  const onPlayPause = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.playPause();
    }
  }, [playerRef.current])

  const onReplay = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.seekTo(0)
    }
  }, [playerRef.current])

  const handleSkipBackward = useCallback(() => {
    if (playerRef.current) {
      const newTime = playerRef.current.getCurrentTime() - 15; // Adjust this value as needed
      playerRef.current.seekTo(newTime / playerRef.current.getDuration());
    }
  }, [playerRef.current]);

  const handleSkipForward = useCallback(() => {
    if (playerRef.current) {
      const newTime = playerRef.current.getCurrentTime() + 30; // Adjust this value as needed
      playerRef.current.seekTo(newTime / playerRef.current.getDuration());
    }
  }, [playerRef.current]);

  const handlePlaybackRateChange = useCallback((rate) => {
    if (playerRef.current) {
      playerRef.current.setPlaybackRate(rate);
      setPlayerState((state) => {
        return {
          ...state,
          playbackRate: rate
        }
      });
    }
  }, [playerRef.current]);

  const handleVolumeChange = useCallback((volume) => {
    if (playerRef.current) {
      playerRef.current.setVolume(volume / 100);
      setPlayerState((state) => {
        return {
          ...state,
          volume: volume
        }
      });
    }
  }, [playerRef.current]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const paddedMins = String(mins).padStart(2, '0');
    const paddedSecs = String(secs).padStart(2, '0');

    if (hrs === 0) {
      return `${paddedMins}:${paddedSecs}`;
    } else {
      const paddedHrs = String(hrs);
      return `${paddedHrs}:${paddedMins}:${paddedSecs}`;
    }
  }

  const createRange = (start) => {
    const newRange = {
      id: new Date().getTime(),
      external_id: null,
      start: start,
      end: start,
      segmentLength: segments,
      synced: false,
      syncing: false,
      updatedDate: new Date().getTime(),
      syncedDate: null
    };

    dispatch({
      type: RECORD_NEW_RANGE,
      progress: start,
      range: newRange
    });

    return newRange;
  }

  const updateRange = (end) => {
    if (!currentRangeRef.current) {
      return;
    }

    const newRange = {
      ...((currentRangeRef.current ?? {}) as object),
      end: end
    }

    if (!_.isEqual(currentRangeRef.current, newRange)) {
      dispatch({
        type: UPDATE_RANGE,
        range: newRange
      });
    }

    return newRange;
  }

  const syncRanges = (currentTime) => {
    dispatch({
      type: SYNC_RANGES,
      progress: currentTime
    });
  }

  return (
    <div className={cx(styles.playerContainer)}>
      {loadingTransition(
        (style, item) =>
          item && (
            <animated.div className={styles.loading} style={style}>
              <AudioVisualizerLoader />
            </animated.div>
          ),
      )}

      <animated.div
        className={cx(styles.player, {
          [styles.waveformLoaded]: playerState.waveformLoaded,
        })}
        style={{
          transform: playerState.waveformLoaded
            ? "translateY(0%)"
            : "translateY(100%)",
          opacity: playerState.waveformLoaded ? 1 : 0,
        }}
      >
        <div className={styles.visualContainer}>
          <div className={styles.track}>
            <div ref={containerRef} className={styles.waveform} />
          </div>

          <div className={styles.timeRow}>
            <div className={styles.timeElapsed}>
              {formatTime(playerState.currentTime || 0)}
            </div>
            <div className={styles.totalTime}>
              {formatTime(playerState.duration || 0)}
            </div>
          </div>
        </div>

        <div className={cx(styles.actions, styles.main)}>
          <VolumeButton
            volume={playerState.volume}
            onChange={(value) => {
              handleVolumeChange(value);
            }}
          />
          <div className={styles.playbackControls}>
            <button
              onClick={handleSkipBackward}
              className={cx(styles.action, styles.rewind)}
            >
              <i className="paperflite-reload"></i>
              <span>15</span>
            </button>
            <button
              onClick={onPlayPause}
              className={cx(styles.action, styles.play)}
            >
              <i
                className={
                  playerState.audioPlaying
                    ? "paperflite-pause"
                    : "paperflite-play"
                }
              ></i>
            </button>
            <button
              onClick={handleSkipForward}
              className={cx(styles.action, styles.forward)}
            >
              <i className="paperflite-reload"></i>
              <span>30</span>
            </button>
          </div>

          <PlayRateButton
            playbackRate={playerState.playbackRate}
            onChange={handlePlaybackRateChange}
          />
        </div>
      </animated.div>
    </div>
  );
}

interface PlayRateButtonProps {
  playbackRate: number;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: number) => void;
}

const PlayRateButton: React.FC<PlayRateButtonProps> = ({ playbackRate, onChange }) => {
  const playRatePopover = useMemo(() => {
    const handleRateChange = (rate: number) => {
      if (onChange) {
        onChange(rate);
      }
    };

    return (
      <Popover id="playrate-popup" className={styles.playRatePopover}>
        <Popover.Body>
          <div className={styles.options}>
            <div className={styles.option} onClick={() => handleRateChange(0.5)}>
              <span className={styles.label}>0.5X</span>
              {playbackRate === 0.5 && <i className="paperflite-tick-selected"></i>}
            </div>
            <div className={styles.option} onClick={() => handleRateChange(1.0)}>
              <span className={styles.label}>1.0X</span>
              {playbackRate === 1 && <i className="paperflite-tick-selected"></i>}
            </div>
            <div className={styles.option} onClick={() => handleRateChange(1.5)}>
              <span className={styles.label}>1.5X</span>
              {playbackRate === 1.5 && <i className="paperflite-tick-selected"></i>}
            </div>
            <div className={styles.option} onClick={() => handleRateChange(2.0)}>
              <span className={styles.label}>2.0X</span>
              {playbackRate === 2.0 && <i className="paperflite-tick-selected"></i>}
            </div>
          </div>
        </Popover.Body>
      </Popover>
    );

  }, [playbackRate, onChange]);

  return (
    <OverlayTrigger trigger="click" placement="top" overlay={playRatePopover} rootClose>
      <button className={styles.speed}>
        <i className="paperflite-circle-dashed-play"></i>
      </button>
    </OverlayTrigger>
  );
}

interface VolumeButtonProps {
  volume: number;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: number) => void;
}

const VolumeButton: React.FC<VolumeButtonProps> = ({ volume, onChange }) => {
  const volumePopover = useMemo(() => {
    const handleVolumeChange = (e) => {
      if (onChange) {
        onChange(parseInt(e.target.value));
      }
    };

    return (
      <Popover id="volume-popup" className={styles.volumePopover}>
        <Popover.Body>
          <div className={styles.volumeControl}>
            <div className={styles.progress} style={{
              height: `calc(${volume}% + ${(volume > 25 ? 0 : 4)}px)`,
              borderTopLeftRadius: volume > 50 ? 4 : 0,
              borderTopRightRadius: volume > 50 ? 4 : 0,
            }} />
            <input type="range"
              min="0" max="100"
              value={volume}
              className={styles.volumeSlider}
              onChange={handleVolumeChange}
            />
          </div>
        </Popover.Body>
      </Popover>
    );

  }, [volume, onChange]);

  return (
    <OverlayTrigger trigger="click" placement="top" overlay={volumePopover} rootClose>
      <button className={styles.volumeButton}>
        <i className="paperflite-volume-on"></i>
      </button>
    </OverlayTrigger>
  );
}

const AudioVisualizerLoader: React.FC = () => {
  const desktop = useDesktop();

  return (
    <div className={styles.audioVisualizerLoader}>
      <SoundVisualizer
        disabled={false}
        volumeLevel={1}
        barCount={desktop ? 80 : 40}
        barWidth="2px"
        height={30}
        color="#FF45C2"
        durationInSeconds={0.25}
        cornerRadius={8}
        verticalAlignment="center"
      />
      <div className={styles.message}>Analyzing segment boundaries...</div>
    </div>
  );
}